import React, { useState, useEffect } from 'react';
import './SlackNavigation.css';
import { motion, AnimatePresence } from 'framer-motion';
import { authService } from '../services/auth';
import TeamCreationPopup from './TeamCreationPopup';
import WorkHub from './WorkHub';
import config from '../config';

const SlackNavigation = ({ 
  campaigns = {}, 
  activeCampaignId, 
  activeFunction, 
  viewMode,
  onCreateCampaign, 
  onCloseCampaign, 
  onSwitchCampaign, 
  onSetActiveFunction,
  onSetViewMode
}) => {
  const [isPanelOpen, setIsPanelOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [showTeamPopup, setShowTeamPopup] = useState(false);
  const [emailType, setEmailType] = useState(null);
  const [isPanelHovered, setIsPanelHovered] = useState(false);


  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check email type and get user info on component mount
  useEffect(() => {
    const checkEmailTypeAndLoadUserInfo = async () => {
      try {
        const user = await authService.getCurrentUser();
        setUserInfo(user);
        
        if (user?.email) {
          const response = await fetch(`${config.apiUrl}/api/team/check-email`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authService.getToken()}`
            },
            body: JSON.stringify({ email: user.email })
          });
          
          if (response.ok) {
            const data = await response.json();
            console.log('Email check response:', data);  // Debug log
            setEmailType(data.is_work_email ? 'work' : 'personal');
            
            // Only show team creation popup for:
            // 1. Work emails with no team role (new teams)
            // 2. Team owners who haven't sent invites yet
            // Never show for team members
            const isDismissed = localStorage.getItem('team_creation_dismissed');
            const shouldShowPopup = data.is_work_email && 
                                   !isDismissed && 
                                   (!data.team_role || (data.team_role === 'owner' && !data.has_sent_invites));
            
            console.log('Team popup check:', {
              is_work_email: data.is_work_email,
              team_role: data.team_role,
              has_sent_invites: data.has_sent_invites,
              dismissed: isDismissed,
              should_show: shouldShowPopup
            });
            
            if (shouldShowPopup) {
              console.log('Showing team popup - work email owner or no team yet');
              setShowTeamPopup(true);
            }
          }
        }
      } catch (error) {
        console.error('Error checking email type:', error);
      }
    };
    
    checkEmailTypeAndLoadUserInfo();
  }, []);

  const getDisplayInfo = () => {
    if (!userInfo) return { initial: 'U', name: 'User' };
    
    if (emailType === 'work' && userInfo.company_name) {
      // For work emails, show company info
      const initial = userInfo.company_name.charAt(0).toUpperCase();
      return { initial, name: userInfo.company_name };
    } else {
      // For personal emails or no company info, show user info
      const name = userInfo.name || userInfo.email?.split('@')[0] || 'User';
      const initial = name.charAt(0).toUpperCase();
      return { initial, name };
    }
  };

  const getFunctionCampaigns = () => {
    return Object.values(campaigns || {}).filter(c => c.function === activeFunction);
  };

  const handleFunctionChange = (functionName) => {
    onSetActiveFunction(functionName);
    const functionCampaigns = Object.values(campaigns || {}).filter(c => c.function === functionName);
    if (functionCampaigns.length > 0) {
      onSwitchCampaign(functionCampaigns[0].id);
    } else {
      onCreateCampaign(functionName);
    }
    if (isMobile) {
      setIsPanelOpen(false);
    }
  };

  const isPanelCollapsed = !isMobile ? !isPanelHovered : false;

  return (
    <div className="slack-navigation">
      {/* Navigation Sidebar - Collapsible */}
      <div className="nav-sidebar">
        {/* Navigation Items */}
        <div className="nav-items-container">
          <button 
            className="nav-item company-logo-item"
            onClick={() => setShowTeamPopup(true)}
            type="button"
            title="Manage team members"
          >
            <div className="add-workspace-btn">
              {getDisplayInfo().initial}
            </div>
            <span className="nav-item-label">{getDisplayInfo().name}</span>
          </button>
          <div className="nav-divider"></div>
          <button 
            className={`nav-item ${activeFunction === 'sales' ? 'active' : ''}`}
            onClick={() => handleFunctionChange('sales')}
            title="Sales"
            type="button"
          >
            <span className="nav-item-icon">S</span>
            <span className="nav-item-label">Sales</span>
          </button>
          <button 
            className={`nav-item ${activeFunction === 'marketing' ? 'active' : ''}`}
            onClick={() => handleFunctionChange('marketing')}
            title="Marketing"
            type="button"
          >
            <span className="nav-item-icon">M</span>
            <span className="nav-item-label">Marketing</span>
          </button>
          <button 
            className={`nav-item ${activeFunction === 'engineering' ? 'active' : ''}`}
            onClick={() => handleFunctionChange('engineering')}
            title="Engineering"
            type="button"
          >
            <span className="nav-item-icon">E</span>
            <span className="nav-item-label">Engineering</span>
          </button>
          <button 
            className="nav-item add-function"
            onClick={() => alert('Add new business function coming soon!')}
            title="Add new function"
            type="button"
          >
            <span className="nav-item-icon">+</span>
            <span className="nav-item-label">Add Function</span>
          </button>
        </div>
      </div>

      {/* Campaign Panel */}
      <AnimatePresence>
        {(isPanelOpen || !isMobile) && (
          <motion.div 
            className={`campaign-panel ${isMobile && !isPanelOpen ? 'collapsed' : ''}`}
            initial={{ x: -240 }}
            animate={{ x: 0 }}
            exit={{ x: -240 }}
            transition={{ duration: 0.2 }}
            onMouseEnter={() => setIsPanelHovered(true)}
            onMouseLeave={() => setIsPanelHovered(false)}
          >
            {/* Work Hub Section */}
            <WorkHub isCollapsed={isPanelCollapsed} />
            
            {/* Work Stream Section */}
            <div className="campaign-header" style={{ borderTop: '1px solid #f0f0f0' }}>
              <span>Work Stream</span>
            </div>
            <div style={{ padding: '0 0 12px 0' }}>
              <button 
                className={`campaign-tab ${viewMode === 'workstream' ? 'active' : ''}`}
                onClick={() => onSetViewMode('workstream')}
                type="button"
              >
                <span className="campaign-tab-name">Flow</span>
              </button>
            </div>
            
            {/* Work History Section */}
            <div className="campaign-header">
              <span>Work History</span>
            </div>
            
            <div className="campaign-tabs">
              {getFunctionCampaigns().map(campaign => (
                <button 
                  key={campaign.id}
                  className={`campaign-tab ${viewMode === 'campaign' && campaign.id === activeCampaignId ? 'active' : ''}`}
                  onClick={() => onSwitchCampaign(campaign.id)}
                  type="button"
                >
                  <span className="campaign-tab-name">{campaign.name}</span>
                  {getFunctionCampaigns().length > 1 && (
                    <button 
                      className="campaign-tab-close"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCloseCampaign(campaign.id);
                      }}
                      aria-label="Close campaign"
                      type="button"
                    >
                      ×
                    </button>
                  )}
                </button>
              ))}
            </div>
            
            {getFunctionCampaigns().length < 50 && (
              <button 
                className="add-campaign-btn"
                onClick={() => onCreateCampaign(activeFunction)}
              >
                + New Campaign
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content - Removed duplicate Dashboard rendering */}
      
      {/* Team Creation Popup */}
      {showTeamPopup && (
        <TeamCreationPopup
          userEmail={userInfo?.email}
          onClose={() => {
            setShowTeamPopup(false);
            localStorage.setItem('team_creation_dismissed', 'true');
          }}
          onSuccess={(teamData) => {
            setUserInfo({ ...userInfo, ...teamData });
            setShowTeamPopup(false);
          }}
        />
      )}
    </div>
  );
};

export default SlackNavigation;